import {identity, memoizeWith, path} from 'ramda'

import {logAndCaptureErrorMessage} from '../../utils/logging/errorHandlers'
import {getCldrDatesLocale} from './getCldrDatesLocale'

/**
 * Loads the correct static `cldr-dates-full` file, based on the provided `intlLocaleCode`,
 * and returns the gregorian calendar configuration.
 * @returns An object containing the gregorian calendar configuration
 * @param intlLocaleCode The locale whose `cldr-dates-full` file will be loaded
 * @param publicUrl The base URL where the application's static files (including the `cldr-dates-full` files) are hosted
 */
export const getGregorianCalendar = memoizeWith(
  identity,
  async (
    intlLocaleCode: string,
    publicUrl: string
  ): Promise<Record<string, unknown> | undefined> => {
    let mappedLocaleCode = getCldrDatesLocale(intlLocaleCode)
    try {
      const gregorianCalendarFolder = `${publicUrl}js/cldr-dates-full/main/${mappedLocaleCode}`
      const result = await fetch(`${gregorianCalendarFolder}/ca-gregorian.json`)

      if (!result.ok) {
        throw new Error(`HTTP ${result.status}`)
      }

      const responseData = await result.json()
      return path(
        ['main', mappedLocaleCode, 'dates', 'calendars', 'gregorian'],
        responseData
      )
    } catch (error) {
      logAndCaptureErrorMessage(
        `An error occurred while loading cldr dates - ${(error as Error).message}`,
        {level: 'error'}
      )
      // fall back to english to prevent safari crash
      mappedLocaleCode = 'en'
      const {default: fallbackCaGregorian} = await import(
        '../config/fallback-ca-gregorian.json'
      )
      return path(
        ['main', mappedLocaleCode, 'dates', 'calendars', 'gregorian'],
        fallbackCaGregorian
      )
    }
  }
)
